/**
 * Professor Review Wizard - Alpine.js Component
 * Full-page wizard for creating/editing professor reviews
 */

import { wizardComponent } from '../../../../shared/components/wizard.js';

export default function professorReviewWizard(config = {}) {
    const baseWizard = wizardComponent({
        totalSteps: 5,
        currentStep: 1,
        draftKey: config.draftKey || `professor_review_${config.professorId}`,
        autoSaveInterval: 30000
    });

    return {
        ...baseWizard,

        // Configuration
        professorId: config.professorId || null,
        professor: config.professor || null,
        existingReview: config.existingReview || null,
        reviewCourses: config.reviewCourses || [],
        userReviewCourses: config.userReviewCourses || [],
        availableTags: config.availableTags || [],

        // Form State
        grading_point: 1.0,
        proficiency_point: 1.0,
        interesting_point: 1.0,
        homework_load_point: 1.0,
        course_id: null,
        course_text: '',
        educational_field_id: null,
        educational_field_text: '',
        grade: 0.0,
        is_recommended: null,
        tags: [],
        exam_sources: [],
        comment: '',

        // UI State
        commentTyped: false,
        liveRate: 1.0,

        // Dropdowns
        educationalFields: [],
        courses: [],
        fieldSearchQuery: '',
        courseSearchQuery: '',
        showFieldDropdown: false,
        showCourseDropdown: false,

        // Override init
        init() {
            // Call base wizard init
            baseWizard.init.call(this);

            // Initialize form data
            this.formData = this.getFormData();
            this.calculateLiveRate();
            
            // Load existing review if editing
            if (this.existingReview) {
                this.loadExistingReview();
            } else if (this.professorId) {
                this.loadUserReview();
            }

            // Load dropdown data
            this.loadEducationalFields();
            this.loadCourses();

            // Watch for rating changes
            this.$watch('grading_point', () => this.calculateLiveRate());
            this.$watch('proficiency_point', () => this.calculateLiveRate());
            this.$watch('interesting_point', () => this.calculateLiveRate());
            this.$watch('homework_load_point', () => this.calculateLiveRate());

            // Hide suggestions when user types
            this.$nextTick(() => {
                const commentTextarea = this.$refs.commentTextarea;
                if (commentTextarea) {
                    commentTextarea.addEventListener('input', () => {
                        this.commentTyped = commentTextarea.value.length > 0;
                    });
                }
            });

            // Initialize emoji picker after delay
            setTimeout(() => {
                this.initEmojiPicker();
            }, 200);
        },

        // Get form data for draft saving
        getFormData() {
            return {
                grading_point: this.grading_point,
                proficiency_point: this.proficiency_point,
                interesting_point: this.interesting_point,
                homework_load_point: this.homework_load_point,
                course_id: this.course_id,
                course_text: this.course_text,
                educational_field_id: this.educational_field_id,
                educational_field_text: this.educational_field_text,
                grade: this.grade,
                is_recommended: this.is_recommended,
                tags: this.tags,
                exam_sources: this.exam_sources,
                comment: this.comment
            };
        },

        // Validate step
        validateStep(step) {
            switch(step) {
                case 1:
                    // Comment validation
                    if (!this.comment || this.comment.trim().length < 10) {
                        this.setErrors({ comment: ['دیدگاه باید حداقل 10 کاراکتر باشد'] });
                        return false;
                    }
                    return true;
                case 2:
                    // Ratings validation (all should be between 1-5)
                    if (this.grading_point < 1 || this.grading_point > 5 ||
                        this.proficiency_point < 1 || this.proficiency_point > 5 ||
                        this.interesting_point < 1 || this.interesting_point > 5 ||
                        this.homework_load_point < 1 || this.homework_load_point > 5) {
                        this.setErrors({ ratings: ['لطفاً تمام امتیازها را وارد کنید'] });
                        return false;
                    }
                    return true;
                case 3:
                    // Tags and educational field validation
                    if (!this.educational_field_id) {
                        this.setErrors({ educational_field_id: ['لطفاً رشته تحصیلی را انتخاب کنید'] });
                        return false;
                    }
                    return true;
                case 4:
                    // Course and grade validation
                    if (!this.course_text) {
                        this.setErrors({ course_title: ['لطفاً نام درس را وارد کنید'] });
                        return false;
                    }
                    if (!this.grade || this.grade < 0 || this.grade > 20) {
                        this.setErrors({ grade: ['لطفاً نمره معتبر (0-20) وارد کنید'] });
                        return false;
                    }
                    if (!this.exam_sources || this.exam_sources.length === 0) {
                        this.setErrors({ exam_sources: ['لطفاً حداقل یک منبع امتحان را انتخاب کنید'] });
                        return false;
                    }
                    return true;
                case 5:
                    // Final validation
                    if (this.is_recommended === null) {
                        this.setErrors({ is_recommended: ['لطفاً انتخاب کنید که آیا این استاد را پیشنهاد می‌دهید یا خیر'] });
                        return false;
                    }
                    return true;
                default:
                    return true;
            }
        },

        // Calculate live overall rating
        calculateLiveRate() {
            this.liveRate = (this.grading_point + this.proficiency_point + this.interesting_point + this.homework_load_point) / 4;
            this.liveRate = parseFloat(this.liveRate.toFixed(1));
        },

        // Load existing review data
        loadExistingReview() {
            if (!this.existingReview) return;

            this.grading_point = parseFloat(this.existingReview.grading_point) || 1.0;
            this.proficiency_point = parseFloat(this.existingReview.proficiency_point) || 1.0;
            this.interesting_point = parseFloat(this.existingReview.interesting_point) || 1.0;
            this.homework_load_point = parseFloat(this.existingReview.homework_load_point) || 1.0;
            this.course_id = this.existingReview.educationalCourse?.id || null;
            this.course_text = this.existingReview.educationalCourse?.title || this.existingReview.course_title || '';
            this.educational_field_id = this.existingReview.educationalField?.id || null;
            this.educational_field_text = this.existingReview.educationalField?.title || '';
            this.grade = parseFloat(this.existingReview.grade) || 0.0;
            this.is_recommended = this.existingReview.is_recommended;
            this.comment = this.existingReview.comment || '';
            this.tags = this.existingReview.tags?.map(t => t.id) || [];
            
            if (this.existingReview.exam_sources) {
                // exam_sources are stored as comma-separated values
                // Handle both legacy numeric format and new English string format
                const sourceValues = this.existingReview.exam_sources.split(',').map(s => s.trim());
                this.exam_sources = sourceValues
                    .map(value => {
                        // Try to parse as English string first (new format)
                        const index = this.getExamSourceIndex(value);
                        if (index !== null) return index;
                        // Fallback to numeric parsing (legacy format)
                        const numIndex = parseInt(value);
                        return !isNaN(numIndex) && numIndex >= 0 && numIndex <= 4 ? numIndex : null;
                    })
                    .filter(index => index !== null);
            }

            this.calculateLiveRate();
        },

        // Load user's existing review
        async loadUserReview() {
            if (!this.professorId) return;

            try {
                const response = await fetch(`/professors/${this.professorId}/review/user`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'same-origin'
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.review) {
                        this.existingReview = data.review;
                        this.loadExistingReview();
                    }
                    // If success: false, no review exists - this is expected for new reviews
                } else {
                    // Other HTTP errors - log but don't break the flow
                    console.warn('Failed to load user review:', response.status, response.statusText);
                }
            } catch (error) {
                // Network errors - log but don't break the flow
                console.warn('Failed to load user review:', error);
            }
        },

        // Load educational fields
        async loadEducationalFields() {
            try {
                const response = await fetch('/api/fields/dynamic', {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    this.educationalFields = data.fields || [];
                } else {
                    const fallbackResponse = await fetch('/api/fields', {
                        method: 'GET',
                        headers: { 'Accept': 'application/json' }
                    });
                    if (fallbackResponse.ok) {
                        const fallbackData = await fallbackResponse.json();
                        if (fallbackData.data && Array.isArray(fallbackData.data)) {
                            this.educationalFields = fallbackData.data.map(item => ({
                                id: item.id,
                                title: item.title || item.name
                            }));
                        }
                    }
                }
            } catch (error) {
                console.warn('Failed to load educational fields:', error);
            }
        },

        // Load courses
        async loadCourses() {
            // Always start with reviewCourses as fallback
            const fallbackCourses = this.reviewCourses.map(title => ({ id: null, title }));
            
            if (!this.professorId) {
                this.courses = fallbackCourses;
                return;
            }

            try {
                const response = await fetch(`/api/professors/${this.professorId}/courses/dynamic`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.courses && data.courses.length > 0) {
                        this.courses = data.courses;
                    } else {
                        // If API returns empty, use fallback
                        this.courses = fallbackCourses;
                    }
                } else {
                    this.courses = fallbackCourses;
                }
            } catch (error) {
                console.warn('Failed to load courses from API, using fallback:', error);
                this.courses = fallbackCourses;
            }
        },

        // Search educational fields
        async searchEducationalFields() {
            if (!this.fieldSearchQuery || this.fieldSearchQuery.length < 2) {
                await this.loadEducationalFields();
                return;
            }

            try {
                const response = await fetch(`/api/fields/search?q=${encodeURIComponent(this.fieldSearchQuery)}`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.data && Array.isArray(data.data)) {
                        this.educationalFields = data.data.map(item => ({
                            id: item.id,
                            title: item.title || item.name
                        }));
                    } else if (data.fields && Array.isArray(data.fields)) {
                        this.educationalFields = data.fields;
                    } else if (Array.isArray(data)) {
                        this.educationalFields = data;
                    }
                }
            } catch (error) {
                console.warn('Failed to search educational fields:', error);
            }
        },

        // Search courses
        async searchCourses() {
            if (!this.courseSearchQuery || this.courseSearchQuery.length < 2) {
                await this.loadCourses();
                return;
            }

            try {
                // First try searching professor's courses via the dynamic endpoint
                if (this.professorId) {
                    const response = await fetch(`/api/professors/${this.professorId}/courses/dynamic`, {
                        method: 'GET',
                        headers: { 'Accept': 'application/json' }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.success && data.courses) {
                            // Filter courses by search query
                            const searchLower = this.courseSearchQuery.toLowerCase();
                            this.courses = data.courses.filter(course => 
                                course.title && course.title.toLowerCase().includes(searchLower)
                            );
                            return;
                        }
                    }
                }

                // Fallback: search all courses via general API
                const response = await fetch(`/api/courses?q=${encodeURIComponent(this.courseSearchQuery)}&professor_id=${this.professorId || ''}`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.data && Array.isArray(data.data)) {
                        this.courses = data.data.map(item => ({
                            id: item.id,
                            title: item.title || item.name
                        }));
                    } else if (data.courses && Array.isArray(data.courses)) {
                        this.courses = data.courses;
                    } else if (Array.isArray(data)) {
                        this.courses = data;
                    } else {
                        // If no results, filter from reviewCourses
                        const searchLower = this.courseSearchQuery.toLowerCase();
                        this.courses = this.reviewCourses
                            .filter(title => title.toLowerCase().includes(searchLower))
                            .map(title => ({ id: null, title }));
                    }
                } else {
                    // Fallback to filtering reviewCourses
                    const searchLower = this.courseSearchQuery.toLowerCase();
                    this.courses = this.reviewCourses
                        .filter(title => title.toLowerCase().includes(searchLower))
                        .map(title => ({ id: null, title }));
                }
            } catch (error) {
                console.warn('Failed to search courses:', error);
                // Fallback to filtering reviewCourses
                const searchLower = this.courseSearchQuery.toLowerCase();
                this.courses = this.reviewCourses
                    .filter(title => title.toLowerCase().includes(searchLower))
                    .map(title => ({ id: null, title }));
            }
        },

        // Handle dropdown selections from searchable dropdown component
        handleDropdownSelected(detail) {
            const displayText = detail.item?.title || detail.item?.name || detail.item?.full_name || detail.displayText || '';
            if (detail.name === 'educational_field_id') {
                this.educational_field_id = detail.value;
                this.educational_field_text = displayText;
            } else if (detail.name === 'course_id') {
                if (detail.item && detail.item.id) {
                    this.course_id = detail.item.id;
                } else {
                    // If no ID, treat as custom course title
                    this.course_id = null;
                    this.course_text = displayText;
                }
                this.course_text = displayText;
            }
        },

        // Toggle tag
        toggleTag(tagId) {
            if (this.tags.includes(tagId)) {
                this.tags = this.tags.filter(id => id !== tagId);
            } else {
                this.tags.push(tagId);
            }
        },

        // Map exam source index to English string (for backend validation)
        getExamSourceString(index) {
            const mapping = {
                0: 'notes',
                1: 'book',
                2: 'class_discussions',
                3: 'sample_questions',
                4: 'irrelevant'
            };
            return mapping[index] || null;
        },

        // Map exam source string back to index (for loading existing reviews)
        getExamSourceIndex(str) {
            const mapping = {
                'notes': 0,
                'book': 1,
                'class_discussions': 2,
                'sample_questions': 3,
                'irrelevant': 4
            };
            return mapping[str] !== undefined ? mapping[str] : null;
        },

        // Toggle exam source
        toggleExamSource(sourceIndex) {
            if (this.exam_sources.includes(sourceIndex)) {
                this.exam_sources = this.exam_sources.filter(id => id !== sourceIndex);
            } else {
                this.exam_sources.push(sourceIndex);
            }
        },

        // Insert suggestion
        insertSuggestion(suggestion) {
            this.comment = suggestion;
            this.commentTyped = true;
            this.$refs.commentTextarea?.focus();
        },

        // Override submit
        async onSubmit(formData) {
            // Prepare form data
            const submitData = {
                review_id: this.existingReview?.id || null,
                course_id: this.course_id,
                course_title: this.course_text,
                educational_field: this.educational_field_id ? String(this.educational_field_id) : null,
                grading_point: this.grading_point,
                proficiency_point: this.proficiency_point,
                interesting_point: this.interesting_point,
                homework_load_point: this.homework_load_point,
                is_recommended: this.is_recommended ? 1 : 0,
                comment: this.comment,
                grade: String(this.grade),
                tags: this.tags,
                exam_sources: this.exam_sources.map(index => this.getExamSourceString(index)).filter(str => str !== null)
            };

            // Remove null/empty values
            Object.keys(submitData).forEach(key => {
                if (submitData[key] === null || submitData[key] === '' || (Array.isArray(submitData[key]) && submitData[key].length === 0)) {
                    delete submitData[key];
                }
            });

            // Determine endpoint
            const endpoint = this.existingReview?.id 
                ? '/professors/review/update'
                : `/professors/${this.professorId}/review`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': this.getCsrfToken()
                },
                credentials: 'same-origin',
                body: JSON.stringify(submitData)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                this.showSuccessMessage(data.message || (this.existingReview?.id ? 'نظر شما با موفقیت ویرایش شد.' : 'نظر شما با موفقیت ثبت شد.'));
                
                window.dispatchEvent(new CustomEvent('review-submitted', {
                    detail: {
                        success: true,
                        message: this.successMessage,
                        review_id: data.review_id
                    }
                }));

                // Redirect after delay
                setTimeout(() => {
                    window.location.href = `/professors/${this.professorId}`;
                }, 2000);
            } else {
                if (data.errors) {
                    this.setErrors(data.errors);
                } else {
                    this.setErrors({ general: [data.message || 'خطا در ثبت نظر. لطفاً دوباره تلاش کنید.'] });
                }
                throw new Error(data.message || 'Submission failed');
            }
        },

        // Get CSRF token
        getCsrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        },

        // Initialize emoji picker
        initEmojiPicker() {
            if (typeof EmojiPicker !== 'undefined' && typeof jQuery !== 'undefined') {
                try {
                    if (!window.emojiPicker) {
                        window.emojiPicker = new EmojiPicker({
                            emojiable_selector: "[data-emojiable=true]",
                            assetsPath: "/assets/emoji-picker/img/",
                            popupButtonClasses: "fa fa-smile-o"
                        });
                    }
                    window.emojiPicker.discover();
                } catch (e) {
                    console.warn('Emoji picker initialization failed:', e);
                }
            }
        }
    };
}
